import React, { useCallback, useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, jeton, messageErreur } from "../api";
import { useApp } from "../App";
import { RefreshCw } from "lucide-react";
import { Btn, Champ, Choix, Micro } from "../components/Bits";
import { Marque } from "../components/Shell";
import { METIERS } from "../lib/format";

export default function Auth({ mode }) {
  const inscription = mode === "inscription";
  const nav = useNavigate();
  const { setCompte } = useApp();

  const [etape, setEtape] = useState("formulaire");
  const [email, setEmail] = useState("");
  const [motDePasse, setMotDePasse] = useState("");
  const [prenom, setPrenom] = useState("");
  const [activite, setActivite] = useState("");
  const [metier, setMetier] = useState("");
  const [envoi, setEnvoi] = useState(false);
  const [erreurs, setErreurs] = useState({});
  const [captcha, setCaptcha] = useState(null);
  const [code, setCode] = useState("");
  const [codeVerif, setCodeVerif] = useState("");
  const [renvoiBloque, setRenvoiBloque] = useState(false);

  const nouveauCaptcha = useCallback(() => {
    setCode("");
    api
      .get("/captcha")
      .then(({ data }) => setCaptcha(data))
      .catch(() => setCaptcha(false));
  }, []);

  useEffect(() => {
    if (inscription) nouveauCaptcha();
  }, [inscription, nouveauCaptcha]);

  const valider = () => {
    const e = {};
    if (!/^[^@\s]+@[^@\s]+\.[a-z]{2,}$/i.test(email.trim())) e.email = "Adresse e-mail incomplète.";
    if (motDePasse.length < (inscription ? 8 : 1))
      e.motDePasse = "Au moins 8 caractères.";
    if (inscription && !prenom.trim()) e.prenom = "Ton prénom, pour te parler correctement.";
    if (inscription && !activite.trim()) e.activite = "Le nom sous lequel tes clientes te connaissent.";
    if (inscription && code.trim().length < 5) e.code = "Recopie les 5 caractères de l’image.";
    setErreurs(e);
    return Object.keys(e).length === 0;
  };

  const entrer = (data) => {
    jeton.ecrire(data.token);
    setCompte(data.compte);
    nav(
      data.compte.role === "admin"
        ? "/admin"
        : data.compte.onboarding_done
          ? "/journee"
          : "/bienvenue",
      { replace: true }
    );
  };

  const envoyer = async (ev) => {
    ev.preventDefault();
    if (envoi || !valider()) return;
    setEnvoi(true);
    try {
      if (inscription) {
        await api.post("/auth/inscription", {
          email: email.trim().toLowerCase(),
          password: motDePasse,
          prenom: prenom.trim(),
          activite: activite.trim(),
          metier: metier || "autre",
          captcha_jeton: captcha?.jeton,
          captcha_reponse: code.trim(),
        });
        setEtape("verification");
      } else {
        const { data } = await api.post("/auth/connexion", {
          email: email.trim().toLowerCase(), password: motDePasse,
        });
        entrer(data);
      }
    } catch (err) {
      toast.error(messageErreur(err, "Connexion impossible."));
      if (inscription) nouveauCaptcha();
    } finally {
      setEnvoi(false);
    }
  };

  const validerCode = async (ev) => {
    ev.preventDefault();
    if (envoi || codeVerif.trim().length !== 6) return;
    setEnvoi(true);
    try {
      const { data } = await api.post("/auth/verifier-inscription", {
        email: email.trim().toLowerCase(), code: codeVerif.trim(),
      });
      entrer(data);
    } catch (err) {
      toast.error(messageErreur(err, "Code invalide."));
    } finally {
      setEnvoi(false);
    }
  };

  const renvoyerCode = async () => {
    if (renvoiBloque) return;
    setRenvoiBloque(true);
    try {
      await api.post("/auth/renvoyer-code", { email: email.trim().toLowerCase() });
      toast.success("Nouveau code envoyé.");
    } catch (err) {
      toast.error(messageErreur(err, "Impossible de renvoyer le code."));
    } finally {
      setTimeout(() => setRenvoiBloque(false), 30000);
    }
  };

  return (
    <div className="min-h-screen flex flex-col" style={{ background: "var(--bg)" }}>
      <Helmet>
        <title>{inscription ? "Créer mon compte — Servae" : "Connexion — Servae"}</title>
        <meta
          name="description"
          content={
            inscription
              ? "Crée ton compte Servae en 5 minutes, sans carte bancaire, et récupère ta page de réservation."
              : "Connecte-toi à ton compte Servae pour retrouver ton agenda et tes clientes."
          }
        />
        <link rel="canonical" href={`https://servae.fr/${inscription ? "inscription" : "connexion"}`} />
        <meta name="robots" content="noindex" />
      </Helmet>
      <header style={{ borderBottom: "1px solid var(--border)" }}>
        <div className="colonne pad h-14 flex items-center justify-between">
          <Link to="/" style={{ color: "var(--text)" }}>
            <Marque lien={false} />
          </Link>
          <Link
            to={inscription ? "/connexion" : "/inscription"}
            className="text-[12px] font-semibold uppercase tracking-[0.11em]"
            style={{ color: "var(--text-muted)" }}
            data-testid="bascule-auth"
          >
            {inscription ? "J'ai déjà un compte" : "Créer un compte"}
          </Link>
        </div>
      </header>

      <div className="colonne pad flex-1 grid md:grid-cols-2 gap-10 md:gap-16 py-10 md:py-16 items-start">
        <div className="hidden md:block pt-6">
          <Micro>{inscription ? "Création de compte" : "Te revoilà"}</Micro>
          <h1 className="display text-[40px] mt-4 max-w-[20ch]">
            {inscription ? "Cinq minutes, et ton agenda tourne." : "Ta journée t'attend."}
          </h1>
          <p className="text-[15px] mt-5 max-w-[42ch] leading-relaxed" style={{ color: "var(--text-muted)" }}>
            {inscription
              ? "Tu règles tes prestations et tes horaires, Servae s'occupe des rappels, des relances et des chiffres. 14 jours complets, sans carte bancaire."
              : "Reprends où tu en étais : tes rendez-vous, tes clientes, tes chiffres."}
          </p>
        </div>

        <form onSubmit={etape === "verification" ? validerCode : envoyer} className="surface p-6 md:p-7 w-full max-w-[440px]" style={{ boxShadow: "var(--shadow-sm)" }}>
          {etape === "verification" ? (
            <>
              <Micro>Vérification</Micro>
              <h2 className="display text-[24px] mt-2">Vérifie ton adresse</h2>
              <p className="text-[14px] mt-3 leading-relaxed" style={{ color: "var(--text-muted)" }}>
                On a envoyé un code à <strong>{email.trim()}</strong>. Vérifie aussi tes spams.
              </p>
              <div className="space-y-4 mt-6">
                <Champ
                  label="Code à 6 chiffres"
                  value={codeVerif}
                  onChange={(e) => setCodeVerif(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  placeholder="123456"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  data-testid="champ-code-verification"
                />
              </div>
              <Btn
                type="submit"
                taille="lg"
                className="w-full mt-6"
                charge={envoi}
                data-testid="btn-valider-code"
              >
                Valider et continuer
              </Btn>
              <button
                type="button"
                onClick={renvoyerCode}
                disabled={renvoiBloque}
                data-testid="btn-renvoyer-code"
                className="text-[13px] font-semibold mt-4 block"
                style={{ color: renvoiBloque ? "var(--text-subtle)" : "var(--accent)" }}
              >
                {renvoiBloque ? "Code renvoyé — patiente un peu" : "Je n'ai rien reçu, renvoyer le code"}
              </button>
              <p className="text-[12px] mt-4 leading-relaxed" style={{ color: "var(--text-subtle)" }}>
                Le code est valable 5 minutes.
              </p>
            </>
          ) : (
            <>
          <Micro className="md:hidden">{inscription ? "Création de compte" : "Connexion"}</Micro>
          <h2 className="display text-[24px] mt-2 md:mt-0">
            {inscription ? "Créer mon compte" : "Se connecter"}
          </h2>

          <div className="space-y-4 mt-6">
            {inscription && (
              <>
                <Champ
                  label="Ton prénom"
                  value={prenom}
                  onChange={(e) => setPrenom(e.target.value)}
                  placeholder="Léa"
                  autoComplete="given-name"
                  erreur={erreurs.prenom}
                  data-testid="champ-prenom"
                />
                <Champ
                  label="Nom de ton activité"
                  value={activite}
                  onChange={(e) => setActivite(e.target.value)}
                  placeholder="Studio Onyx"
                  indice="C'est ce que verront tes clientes dans les rappels"
                  erreur={erreurs.activite}
                  data-testid="champ-activite"
                />
                <Choix
                  label="Ton métier"
                  valeur={metier}
                  surChangement={setMetier}
                  placeholder="Choisir"
                  testid="choix-metier"
                  options={METIERS.map((m) => ({ valeur: m.code, label: m.label }))}
                />
              </>
            )}
            <Champ
              label="Adresse e-mail"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="toi@exemple.fr"
              autoComplete="email"
              erreur={erreurs.email}
              data-testid="champ-email"
            />
            <Champ
              label="Mot de passe"
              type="password"
              value={motDePasse}
              onChange={(e) => setMotDePasse(e.target.value)}
              placeholder={inscription ? "8 caractères minimum" : "••••••••"}
              autoComplete={inscription ? "new-password" : "current-password"}
              erreur={erreurs.motDePasse}
              data-testid="champ-motdepasse"
            />
            {inscription && captcha !== false && (
              <div>
                <Micro className="mb-2 block">Recopie ce code</Micro>
                <div className="flex items-center gap-3">
                  {captcha ? (
                    <img
                      src={captcha.image}
                      alt="Code à recopier"
                      data-testid="captcha-image"
                      className="h-[62px]"
                      style={{
                        borderRadius: "var(--radius-md)",
                        border: "1px solid var(--border)",
                      }}
                    />
                  ) : (
                    <div
                      className="h-[62px] w-[176px]"
                      style={{ background: "var(--surface-sunken)", borderRadius: "var(--radius-md)" }}
                    />
                  )}
                  <button
                    type="button"
                    onClick={nouveauCaptcha}
                    title="Une autre image"
                    data-testid="captcha-recharger"
                    className="inline-flex h-10 w-10 items-center justify-center shrink-0"
                    style={{
                      borderRadius: "var(--radius-full)",
                      border: "1px solid var(--border)",
                      color: "var(--text-muted)",
                    }}
                  >
                    <RefreshCw size={15} />
                  </button>
                </div>
                <Champ
                  className="mt-3"
                  label=""
                  value={code}
                  onChange={(e) => setCode(e.target.value.toUpperCase().slice(0, 5))}
                  placeholder="Les 5 caractères"
                  erreur={erreurs.code}
                  data-testid="champ-captcha"
                />
              </div>
            )}
          </div>

          <Btn
            type="submit"
            taille="lg"
            className="w-full mt-6"
            charge={envoi}
            data-testid="btn-valider-auth"
          >
            {inscription ? "Créer mon compte" : "Entrer"}
          </Btn>

          <p className="text-[12px] mt-4 leading-relaxed" style={{ color: "var(--text-subtle)" }}>
            {inscription
              ? "Aucune carte bancaire. Tu restes propriétaire de tes données et de ta clientèle."
              : "Tes identifiants sont chiffrés. Ton mot de passe n'est jamais stocké en clair."}
          </p>
            </>
          )}
        </form>
      </div>
    </div>
  );
}
